import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Users, Plus, Search, Upload, Download, Trash2, Edit2, X, Save, Ban, CheckCircle, ChevronUp, ChevronDown, Mail, Send } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const INDUSTRIES = [
  'Oil & Gas', 'Wastewater', 'Water Treatment', 'Power Generation',
  'Manufacturing', 'Marine', 'Chemical', 'Pulp & Paper', 'Mining',
  'Food & Beverage', 'Government', 'Construction',
  'University / Education', 'Healthcare / Hospital', 'Aerospace / Defense',
  'Transportation', 'Hospitality', 'Real Estate / Property Mgmt',
  'Vendor', 'Other'
];

const SortHeader = ({ field, label, sortField, sortDir, onSort, className = '' }) => (
  <th className={`text-left px-4 py-3 font-medium text-gray-600 cursor-pointer select-none hover:bg-gray-100 ${className}`}
    onClick={() => onSort(field)} data-testid={`sort-${field}`}>
    <span className="inline-flex items-center gap-1">
      {label}
      {sortField === field ? (sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />) : <ChevronUp size={12} className="opacity-20" />}
    </span>
  </th>
);

const CRMContacts = () => {
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [industryFilter, setIndustryFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [sortField, setSortField] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [statusFilter, setStatusFilter] = useState('active'); // 'active' | 'excluded' | 'all'
  const [optInFilter, setOptInFilter] = useState(''); // '' | 'opted_in' | 'pending' | 'opted_out' | 'none'
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [form, setForm] = useState({ email: '', name: '', phone: '', title: '', company: '', industry: '', notes: '', tags: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ search, limit: '1000' });
      if (statusFilter === 'excluded') params.set('excluded', 'true');
      else if (statusFilter === 'active') params.set('excluded', 'false');
      if (optInFilter) params.set('opt_in_status', optInFilter);
      const res = await fetch(`${API}/api/crm/contacts?${params}`, { credentials: 'include' });
      const data = await res.json();
      let filtered = data.contacts;
      if (industryFilter) {
        filtered = filtered.filter(c => (c.industry || '').toLowerCase() === industryFilter.toLowerCase());
      }
      filtered.sort((a, b) => {
        // Special-case opt-in sort: opted_in (4) > pending (3) > none (2) > opted_out (1)
        if (sortField === 'opt_in_status') {
          const rank = (s) => s === 'opted_in' ? 4 : s === 'pending' ? 3 : s === 'opted_out' ? 1 : 2;
          const aR = rank(a.opt_in_status), bR = rank(b.opt_in_status);
          if (aR !== bR) return sortDir === 'asc' ? aR - bR : bR - aR;
          return (a.name || '').localeCompare(b.name || '');
        }
        const aVal = (a[sortField] || '').toString().toLowerCase();
        const bVal = (b[sortField] || '').toString().toLowerCase();
        return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      });
      setContacts(filtered);
      setTotal(data.total);
      setSelectedIds(new Set());
    } catch { toast.error('Failed to load contacts'); }
    finally { setLoading(false); }
  }, [search, industryFilter, sortField, sortDir, statusFilter, optInFilter]);

  useEffect(() => { load(); }, [load]);

  const toggleSelectAll = () => {
    if (selectedIds.size === contacts.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(contacts.map(c => c.id)));
  };
  const toggleSelect = (id) => {
    const s = new Set(selectedIds);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelectedIds(s);
  };
  const handleBulkExclude = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Exclude ${selectedIds.size} contact(s) from all future marketing?`)) return;
    try {
      const res = await fetch(`${API}/api/crm/contacts/bulk-exclude`, { credentials: 'include',
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: Array.from(selectedIds) }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      toast.success(`Excluded ${data.updated} contact(s)`);
      load();
    } catch (err) { toast.error(err.message || 'Bulk exclude failed'); }
  };
  const handleBulkInclude = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Re-include ${selectedIds.size} contact(s) on the active list?`)) return;
    try {
      const res = await fetch(`${API}/api/crm/contacts/bulk-include`, { credentials: 'include',
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: Array.from(selectedIds) }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      toast.success(`Re-included ${data.updated} contact(s)`);
      load();
    } catch (err) { toast.error(err.message || 'Bulk include failed'); }
  };
  const handleExport = () => {
    const params = new URLSearchParams();
    if (statusFilter === 'excluded') params.set('excluded', 'true');
    else if (statusFilter === 'active') params.set('excluded', 'false');
    if (search) params.set('search', search);
    window.open(`${API}/api/crm/contacts/export?${params}`, '_blank');
  };

  const handleSendOptIn = async () => {
    if (!window.confirm('Send the Opt-In email to ALL active contacts that have not yet responded? (You cannot undo this.)')) return;
    try {
      const res = await fetch(`${API}/api/crm/contacts/send-opt-in`, { credentials: 'include',
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ test_only: false }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      toast.success(`Opt-in campaign sent: ${data.sent} delivered, ${data.failed} failed, ${data.total} targeted`);
      load();
    } catch (err) { toast.error(err.message || 'Opt-in send failed'); }
  };

  const handleSendOptInTest = async () => {
    const email = window.prompt('Send a TEST opt-in email to this address:', 'jasonkraft@bcs-us.com');
    if (!email) return;
    try {
      const res = await fetch(`${API}/api/crm/contacts/send-opt-in`, { credentials: 'include',
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ test_only: true, test_email: email }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      toast.success(`Test opt-in sent to ${email}`);
    } catch (err) { toast.error(err.message || 'Test send failed'); }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const body = { ...form, tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [] };
    try {
      const url = editing ? `${API}/api/crm/contacts/${editing}` : `${API}/api/crm/contacts`;
      const res = await fetch(url, {
        method: editing ? 'PUT' : 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail); }
      toast.success(editing ? 'Contact updated' : 'Contact created');
      setShowForm(false); setEditing(null); setForm({ email: '', name: '', phone: '', title: '', company: '', industry: '', notes: '', tags: '' });
      await load();
    } catch (err) { toast.error(err.message); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this contact?')) return;
    try {
      await fetch(`${API}/api/crm/contacts/${id}`, { credentials: 'include', method: 'DELETE' });
      toast.success('Deleted');
      await load();
    } catch { toast.error('Failed to delete'); }
  };

  const handleEdit = (c) => {
    setEditing(c.id);
    setForm({ email: c.email, name: c.name || '', phone: c.phone || '', title: c.title || '', company: c.company || '', industry: c.industry || '', notes: c.notes || '', tags: (c.tags || []).join(', ') });
    setShowForm(true);
  };

  const handleToggleExclude = async (contact) => {
    const newVal = !contact.excluded;
    try {
      const res = await fetch(`${API}/api/crm/contacts/${contact.id}`, { credentials: 'include',
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ excluded: newVal }),
      });
      if (!res.ok) throw new Error();
      toast.success(newVal ? `${contact.name || contact.email} excluded from marketing` : `${contact.name || contact.email} included in marketing`);
      await load();
    } catch { toast.error('Failed to update'); }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await fetch(`${API}/api/crm/contacts/import`, { credentials: 'include', method: 'POST', body: fd });
      const data = await res.json();
      // After every import, automatically run dedupe across the whole DB so duplicates can never accumulate
      let dedupeNote = '';
      try {
        const dRes = await fetch(`${API}/api/crm/contacts/dedupe`, { credentials: 'include', method: 'POST' });
        if (dRes.ok) {
          const d = await dRes.json();
          if (d.rows_removed > 0) {
            dedupeNote = ` · 🧹 merged ${d.rows_removed} existing duplicate${d.rows_removed !== 1 ? 's' : ''}`;
          }
        }
      } catch (_) { /* dedupe is best-effort */ }
      toast.success(`Imported ${data.imported}, skipped ${data.skipped} dupes${data.errors?.length ? `, ${data.errors.length} errors` : ''}${dedupeNote}`);
      await load();
    } catch { toast.error('Import failed'); }
    e.target.value = '';
  };

  const handleDedupe = async () => {
    if (!window.confirm('Scan the CRM for duplicate contacts (by email) and merge them?\n\n• The OLDEST contact is kept (preserves opt-in history)\n• Tags & notes from duplicates are merged into the keeper\n• The best opt-in status is preserved\n• Younger duplicate rows are deleted')) return;
    try {
      const res = await fetch(`${API}/api/crm/contacts/dedupe`, { credentials: 'include', method: 'POST' });
      if (!res.ok) throw new Error('Dedupe request failed');
      const d = await res.json();
      if (d.rows_removed === 0) {
        toast.success('No duplicates found — your contact list is already clean.');
      } else {
        toast.success(`🧹 Merged ${d.duplicate_groups_found} duplicate group${d.duplicate_groups_found !== 1 ? 's' : ''} · removed ${d.rows_removed} extra row${d.rows_removed !== 1 ? 's' : ''}`);
      }
      await load();
    } catch (e) {
      toast.error(`Dedupe failed: ${e.message}`);
    }
  };

  return (
    <div className="space-y-4">
      {/* Status Tabs — Active / Excluded / All */}
      <div className="flex gap-1 border-b border-gray-200">
        {[
          { key: 'active', label: 'Active', color: 'emerald' },
          { key: 'excluded', label: 'Excluded', color: 'red' },
          { key: 'all', label: 'All Contacts', color: 'indigo' },
        ].map(tab => (
          <button
            key={tab.key}
            data-testid={`contact-tab-${tab.key}`}
            onClick={() => { setStatusFilter(tab.key); setSelectedIds(new Set()); }}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              statusFilter === tab.key
                ? tab.color === 'red' ? 'border-red-600 text-red-700 bg-red-50/40'
                : tab.color === 'emerald' ? 'border-emerald-600 text-emerald-700 bg-emerald-50/40'
                : 'border-indigo-600 text-indigo-700 bg-indigo-50/40'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {tab.label}
            {statusFilter === tab.key && (
              <span className="ml-2 inline-flex items-center justify-center text-xs font-semibold bg-white rounded-full px-2 py-0.5 border border-current">
                {contacts.length}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-1 min-w-[200px]">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-2.5 text-gray-400" size={16} />
            <input data-testid="contact-search" type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder={`Search ${statusFilter === 'excluded' ? 'excluded ' : statusFilter === 'all' ? '' : 'active '}contacts...`} className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none" />
          </div>
          <select
            data-testid="contact-industry-filter"
            value={industryFilter}
            onChange={e => setIndustryFilter(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
          >
            <option value="">All Industries</option>
            {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
          </select>
          <select
            data-testid="contact-optin-filter"
            value={optInFilter}
            onChange={e => setOptInFilter(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
          >
            <option value="">All Opt-In Statuses</option>
            <option value="opted_in">✓ Opted In</option>
            <option value="pending">⏱ Pending</option>
            <option value="opted_out">✗ Opted Out</option>
            <option value="none">— Never Sent</option>
          </select>
          <span className="text-sm text-gray-500">{contacts.length}{industryFilter ? ` in ${industryFilter}` : ''} / {total} total</span>
          <span className="text-sm font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-0.5" data-testid="optin-counter">
            ✓ {contacts.filter(c => c.opt_in_status === 'opted_in').length} opted in
          </span>
          {/* Quick action: jump to Excluded tab + Opted-In filter so user can re-include people who confirmed */}
          <button
            type="button"
            data-testid="find-excluded-optedin-btn"
            onClick={() => { setStatusFilter('excluded'); setOptInFilter('opted_in'); setSelectedIds(new Set()); }}
            className="text-xs font-semibold text-rose-700 bg-rose-50 border border-rose-200 rounded-full px-2.5 py-1 hover:bg-rose-100 transition-colors"
            title="Show contacts who are Excluded but have Opted-In so you can re-include them"
          >
            ⚠ Find Excluded + Opted-In
          </button>
        </div>
        <div className="flex gap-2">
          <Button data-testid="dedupe-contacts-btn" size="sm" variant="outline" onClick={handleDedupe} title="Scan for duplicate emails and merge them — keeps the oldest record">
            🧹 Clean Duplicates
          </Button>
          <Button data-testid="export-contacts-btn" size="sm" variant="outline" onClick={handleExport}>
            <Download size={16} className="mr-1" /> Export
          </Button>
          <label className="cursor-pointer inline-flex items-center gap-1 px-3 py-1.5 border rounded-md text-sm font-medium hover:bg-gray-50 transition-colors" data-testid="import-contacts-btn">
            <input type="file" accept=".xlsx,.csv" className="hidden" onChange={handleImport} />
            <Upload size={16} /> Import
          </label>
          <Button data-testid="add-contact-btn" size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={() => { setEditing(null); setForm({ email: '', name: '', phone: '', title: '', company: '', industry: '', notes: '', tags: '' }); setShowForm(true); }}>
            <Plus size={16} className="mr-1" /> Add Contact
          </Button>
        </div>
      </div>

      {/* Bulk action bar — shown when any contacts are selected */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="bulk-action-bar">
          <span className="text-sm font-medium text-amber-900">{selectedIds.size} selected</span>
          <div className="flex gap-2 ml-auto">
            {statusFilter === 'excluded' ? (
              <Button data-testid="bulk-include-btn" size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={handleBulkInclude}>
                <CheckCircle size={14} className="mr-1" /> Re-Include Selected
              </Button>
            ) : (
              <Button data-testid="bulk-exclude-btn" size="sm" variant="outline" className="border-red-600 text-red-700 hover:bg-red-50" onClick={handleBulkExclude}>
                <Ban size={14} className="mr-1" /> Exclude Selected
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())}>
              <X size={14} className="mr-1" /> Clear
            </Button>
          </div>
        </div>
      )}

      {showForm && (
        <Card className="border-indigo-200">
          <CardContent className="pt-5">
            <form onSubmit={handleSave} className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input data-testid="contact-email" required type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="Email *" className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none" />
              <input data-testid="contact-name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Full Name" className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none" />
              <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="Phone" className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none" />
              <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Job Title" className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none" />
              <input data-testid="contact-company" value={form.company} onChange={e => setForm(f => ({ ...f, company: e.target.value }))} placeholder="Company" className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none" />
              <select data-testid="contact-industry" value={form.industry} onChange={e => setForm(f => ({ ...f, industry: e.target.value }))} className="px-3 py-2 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-indigo-500 focus:outline-none">
                <option value="">Select Industry</option>
                {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
              </select>
              <input value={form.tags} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))} placeholder="Tags (comma-separated)" className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none" />
              <input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Notes" className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none" />
              <div className="md:col-span-2 flex gap-2">
                <Button type="submit" size="sm" className="bg-indigo-600 hover:bg-indigo-700"><Save size={16} className="mr-1" /> {editing ? 'Update' : 'Create'}</Button>
                <Button type="button" variant="outline" size="sm" onClick={() => { setShowForm(false); setEditing(null); }}><X size={16} className="mr-1" /> Cancel</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {loading ? <div className="text-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto" /></div>
      : contacts.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-gray-400"><Users className="mx-auto mb-2" size={32} />
          <p>
            {statusFilter === 'excluded'
              ? (industryFilter ? `No excluded contacts in ${industryFilter}` : 'No excluded contacts — your list is clean.')
              : statusFilter === 'active'
              ? (industryFilter ? `No active contacts in ${industryFilter}` : 'No active contacts yet')
              : (industryFilter ? `No contacts in ${industryFilter}` : 'No contacts')}
          </p>
        </CardContent></Card>
      ) : (
        <div className="bg-white border rounded-lg overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-3 py-3 w-10">
                  <input type="checkbox" data-testid="select-all-contacts"
                    checked={contacts.length > 0 && selectedIds.size === contacts.length}
                    onChange={toggleSelectAll} className="rounded" />
                </th>
                <SortHeader field="name" label="Name" sortField={sortField} sortDir={sortDir} onSort={(f) => { setSortDir(sortField === f && sortDir === 'asc' ? 'desc' : 'asc'); setSortField(f); }} />
                <SortHeader field="email" label="Email" sortField={sortField} sortDir={sortDir} onSort={(f) => { setSortDir(sortField === f && sortDir === 'asc' ? 'desc' : 'asc'); setSortField(f); }} />
                <SortHeader field="company" label="Company" sortField={sortField} sortDir={sortDir} onSort={(f) => { setSortDir(sortField === f && sortDir === 'asc' ? 'desc' : 'asc'); setSortField(f); }} className="hidden md:table-cell" />
                <SortHeader field="phone" label="Phone" sortField={sortField} sortDir={sortDir} onSort={(f) => { setSortDir(sortField === f && sortDir === 'asc' ? 'desc' : 'asc'); setSortField(f); }} className="hidden lg:table-cell" />
                <SortHeader field="industry" label="Industry" sortField={sortField} sortDir={sortDir} onSort={(f) => { setSortDir(sortField === f && sortDir === 'asc' ? 'desc' : 'asc'); setSortField(f); }} className="hidden lg:table-cell" />
                <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
                <SortHeader field="opt_in_status" label="Opt-In" sortField={sortField} sortDir={sortDir} onSort={(f) => { setSortDir(sortField === f && sortDir === 'desc' ? 'asc' : 'desc'); setSortField(f); }} />
                <th className="text-right px-4 py-3 font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {contacts.map(c => (
                <tr key={c.id} className={`border-b last:border-0 hover:bg-gray-50 ${c.excluded ? 'opacity-60' : ''} ${selectedIds.has(c.id) ? 'bg-amber-50' : ''}`}>
                  <td className="px-3 py-3">
                    <input type="checkbox" data-testid={`select-contact-${c.id}`}
                      checked={selectedIds.has(c.id)} onChange={() => toggleSelect(c.id)} className="rounded" />
                  </td>
                  <td className="px-4 py-3 font-medium text-slate-900">{c.name || '—'}</td>
                  <td className="px-4 py-3 text-gray-600">{c.email}</td>
                  <td className="px-4 py-3 text-gray-600 hidden md:table-cell">{c.company || '—'}</td>
                  <td className="px-4 py-3 text-gray-600 hidden lg:table-cell">{c.phone || '—'}</td>
                  <td className="px-4 py-3 hidden lg:table-cell">
                    {c.industry ? <Badge className={`text-xs ${c.industry === 'Vendor' ? 'bg-amber-50 text-amber-700' : 'bg-blue-50 text-blue-700'}`}>{c.industry}</Badge> : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    {c.excluded ? (
                      <Badge className="bg-red-50 text-red-600 text-xs">Excluded</Badge>
                    ) : (
                      <Badge className="bg-emerald-50 text-emerald-700 text-xs">Active</Badge>
                    )}
                  </td>
                  <td className="px-4 py-3" data-testid={`optin-status-${c.id}`}>
                    {c.opt_in_status === 'opted_in' && <Badge className="bg-emerald-100 text-emerald-800 text-xs">✓ Opted In{c.opted_in_at ? ` · ${c.opted_in_at.slice(0,10)}` : ''}</Badge>}
                    {c.opt_in_status === 'pending' && <Badge className="bg-amber-100 text-amber-800 text-xs">⏱ Pending{c.opt_in_sent_at ? ` · ${c.opt_in_sent_at.slice(0,10)}` : ''}</Badge>}
                    {c.opt_in_status === 'opted_out' && <Badge className="bg-rose-100 text-rose-800 text-xs">✗ Opted Out</Badge>}
                    {!c.opt_in_status && <span className="text-gray-400 text-xs">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <Button variant="ghost" size="sm" onClick={() => handleToggleExclude(c)} title={c.excluded ? 'Include in marketing' : 'Exclude from marketing'} data-testid={`toggle-exclude-${c.id}`}>
                      {c.excluded ? <CheckCircle size={14} className="text-emerald-600" /> : <Ban size={14} className="text-gray-400" />}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(c)}><Edit2 size={14} /></Button>
                    <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDelete(c.id)}><Trash2 size={14} /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CRMContacts;
